import * as Sentry from '@sentry/browser';

// Every user-visible string of the dashboard's error handling, word for word from the plan's copy table (rows dash.*).
export const COPY = {
    dash: {
        network: 'Could not reach the server. Check your connection and try again.',
        project_locked: 'The project is busy with another change. Please try again in a moment.',
        project_not_converted: "This project isn't ready yet. Please try again in a moment.",
        project_conversion_failed: "This project couldn't be prepared for the editor. Please contact support.",
        project_on_file_store: 'This project has moved to the new editor. Reload the page and try again.',
        tooManyRequests: 'Too many requests. Please wait a moment and try again.',
        unavailable: 'The server is temporarily unavailable. Please try again in a moment.',
        store_error: 'Something went wrong on the server. Please try again, and contact support if it continues.',
        version_missing: 'The exported file could not be found. Please export again.',
        notFound: "That wasn't found. Reload the page and try again.",
        generic: 'Something went wrong. Please try again, and contact support if it continues.',
        processing: {
            title: 'Processing',
            body: 'Your file is being processed. This page updates automatically.',
        },
        takingLonger: 'This is taking longer than expected.',
        refresh: 'Refresh',
        wizardNotAvailable: "This template isn't available for this file version.",
        wizardDoesNotFit: "This template doesn't fit this file's maps and can't be applied. Please open a support ticket.",
        templateNotApplied: "The template wasn't applied. Reload the project and try again.",
        wizardsPartial: (applied, failed, notAttempted) => (
            `Applied: ${applied.length ? applied.join(', ') : 'none'}. Not applied: ${failed}. ` +
            `Not attempted: ${notAttempted.length ? notAttempted.join(', ') : 'none'}. Select them again to retry.`
        ),
    },
};

// The copy for a 400/422 E1 body: [route suffix, {code: copy}] entries, matched with route.endsWith(suffix).
export const ROUTE_COPY = [
    ['/applyWizard', {
        wizard_not_applicable: COPY.dash.wizardNotAvailable,
        invalid_values: COPY.dash.wizardDoesNotFit,
    }],
];

const routeCopy = (route, code) => {
    const entry = typeof route === 'string' ? ROUTE_COPY.find(([suffix]) => route.endsWith(suffix)) : undefined;
    return entry ? entry[1][code] : undefined;
};

const CODE_409_COPY = {
    project_locked: COPY.dash.project_locked,
    project_not_converted: COPY.dash.project_not_converted,
    project_conversion_failed: COPY.dash.project_conversion_failed,
    project_on_file_store: COPY.dash.project_on_file_store,
};

// The body as an object. A Blob, a string (an HTML or plain-text page) or nothing is {}: none of them is copy.
const bodyOf = (error) => {
    const data = error.response && error.response.data;
    const isBlob = typeof Blob !== 'undefined' && data instanceof Blob;
    return data !== null && typeof data === 'object' && !isBlob ? data : {};
};

const nonEmptyString = (value) => (typeof value === 'string' && value !== '' ? value : undefined);

// The API's machine code (an E1 body's `error`), never shown to a user.
const codeOf = (error) => {
    const body = bodyOf(error);
    return typeof body.error === 'string' ? body.error : undefined;
};

const isE1 = (body) => typeof body.error === 'string' && typeof body.message === 'string';

const result = (message, logout = false, extraToast = null) => ({message, extraToast, logout});

/**
 * What to tell the user about a failed request. Pure; `error` is an axios error and `error.response` may be absent.
 * @returns {{message: string, extraToast: string|null, logout: boolean}}
 */
export const describeError = (error, {route} = {}) => {
    if (!error || !error.response) {
        return result(COPY.dash.network);
    }
    const status = error.response.status;
    const body = bodyOf(error);
    const code = codeOf(error);

    if (status === 401) {
        return result('Your session has expired', true);
    }
    if (status === 403) {
        return result(nonEmptyString(body['hydra:description']) || nonEmptyString(body.text) || 'Error 403. Please contact support.');
    }
    if (status === 400 || status === 422) {
        if (isE1(body)) {
            return result(routeCopy(route, code) || COPY.dash.generic);
        }
        if (body['hydra:description']) {
            return result(body['hydra:description']);
        }
        if (Array.isArray(body.violations) && body.violations[0] && body.violations[0].message) {
            return result(body.violations[0].message);
        }
        if (typeof body.message === 'string' && body.message) {
            return result(body.message);
        }
        return result(COPY.dash.generic);
    }
    if (status === 404) {
        if (code === 'version_missing') {
            return result(COPY.dash.version_missing);
        }
        if (body['hydra:description']) {
            return result(body['hydra:description']);
        }
        return result(COPY.dash.notFound);
    }
    if (status === 405) {
        return result(COPY.dash.generic);
    }
    if (status === 409) {
        return result(CODE_409_COPY[code] || COPY.dash.generic);
    }
    if (status === 413) {
        return result(`The file you tried to upload is too large. ${status}`);
    }
    if (status === 429) {
        return result(COPY.dash.tooManyRequests);
    }
    if (status === 500) {
        return result(code === 'store_error' ? COPY.dash.store_error : 'Error code: 500 - Internal Server Error');
    }
    if (status === 502 || status === 503 || status === 504) {
        return result(COPY.dash.unavailable);
    }
    return result(COPY.dash.generic);
};

/** True for a 409 `project_locked`: the project is busy and the call may be retried. */
export const isLocked409 = (error) => (
    error.response?.status === 409 && error.response.data?.error === 'project_locked'
);

const readBlobText = (blob) => {
    if (typeof blob.text === 'function') {
        return blob.text();
    }
    // jsdom 11.12 (CRA 3.4's test environment) has no Blob.text
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
};

/**
 * A failed Blob download carries its error body as a Blob. Reads it and replaces `error.response.data` with the parsed JSON, or
 * `{text}` when it is not JSON (the export 403 is plain text). Returns the same error object.
 */
export const parseBlobError = async (error) => {
    const data = error && error.response && error.response.data;
    if (typeof Blob === 'undefined' || !(data instanceof Blob)) {
        return error;
    }
    let text;
    try {
        text = await readBlobText(data);
    } catch (readError) {
        return error;
    }
    try {
        error.response.data = JSON.parse(text);
    } catch (parseError) {
        error.response.data = {text};
    }
    return error;
};

/**
 * Reports a failed request to Sentry as a new Error carrying status, url, method and code. Never the axios error itself: its
 * `config` holds the Authorization header.
 */
export const reportToSentry = (error) => {
    // A client-side exception (no config, request or response) carries no Authorization header: report it as itself, with its
    // message and stack. Keyed on the absence of `config`, not on isAxiosError.
    if (error instanceof Error && !error.config && !error.request && !error.response) {
        Sentry.captureException(error);
        return;
    }
    const status = error && error.response ? error.response.status : undefined;
    const url = error && error.config ? error.config.url : undefined;
    const method = error && error.config ? error.config.method : undefined;
    const code = error && error.response ? codeOf(error) : undefined;
    Sentry.withScope((scope) => {
        scope.setExtras({status, url, method, code});
        Sentry.captureException(new Error(`API ${method} ${url} -> ${status ?? 'no response'} ${code ?? ''}`));
    });
};
